import { useQuery } from "@tanstack/react-query";

import { copyTrading } from "@/api/platformAdmin";
import { Badge, Card, CardBody, CardHeader, EmptyState } from "@/components/ui";

/**
 * Copy-trading oversight — all leader profiles (incl. non-public) and active
 * follower subscriptions in the org. Read-only operator/compliance view.
 */
export function AdminCopyTradingPage() {
  const traders = useQuery({ queryKey: ["copy-traders"], queryFn: () => copyTrading.traders() });
  const follows = useQuery({ queryKey: ["copy-follows"], queryFn: () => copyTrading.follows() });

  return (
    <div className="space-y-3">
      <h1 className="text-base font-semibold text-term-fg">Copy trading</h1>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Leaders</span>
        </CardHeader>
        <CardBody className="p-0">
          {traders.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !traders.data || traders.data.length === 0 ? (
            <EmptyState title="No leaders" description="Opt-in copyable trader profiles appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Name</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">User</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Perf fee bps</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Flags</th>
                </tr>
              </thead>
              <tbody>
                {traders.data.map((t) => (
                  <tr key={t.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-medium text-term-fg">{t.display_name}</td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {t.user_id ? t.user_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{t.performance_fee_bps}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex gap-1">
                        <Badge tone={t.public ? "good" : "neutral"}>{t.public ? "Public" : "Private"}</Badge>
                        {t.verified && <Badge tone="good">Verified</Badge>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Follows</span>
        </CardHeader>
        <CardBody className="p-0">
          {!follows.data || follows.data.length === 0 ? (
            <EmptyState title="No follows" description="Active copy subscriptions appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Follower</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Leader</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Copy factor</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">State</th>
                </tr>
              </thead>
              <tbody>
                {follows.data.map((f) => (
                  <tr key={f.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {f.follower_user_id ? f.follower_user_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {f.leader_trader_id ? f.leader_trader_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{f.copy_factor}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={f.stopped_at ? "neutral" : "good"}>
                        {f.stopped_at ? "Stopped" : "Active"}
                      </Badge>
                      {f.stop_reason && <span className="ml-1 text-2xs text-term-mute">{f.stop_reason}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
